import type { MeResponse } from "@safeplate/contracts";
import { buttonVariants, cn } from "@safeplate/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Navigate,
  Outlet,
  redirect,
  useNavigate,
  type ErrorComponentProps,
} from "@tanstack/react-router";

import { getCurrentUser } from "./api/client.js";
import { ApiError, displayError } from "./api/errors.js";
import { AuthScreen, ForgotPasswordScreen } from "./auth/auth-screen.js";
import { InvitationAcceptScreen } from "./auth/invitation-accept-screen.js";
import { pendingInvitationToken } from "./auth/invitations.js";
import {
  currentUserRetryDelay,
  shouldRetryCurrentUser,
} from "./components/slow-start.js";
import { ErrorState, LoadingState } from "./components/states.js";
import { AssetLabelPage, AssetLabelsPage } from "./labels/asset-label-page.js";
import { OrganisationOnboarding } from "./onboarding/organisation-onboarding.js";
import {
  AttentionPage,
  BusinessSettingsPage,
  ComputerSettingsPage,
  DataSettingsPage,
  InboxPage,
  LoggingPage,
  NotificationsSettingsPage,
  PlanSettingsPage,
  PrivacySettingsPage,
  RecordsPage,
  ReportsPage,
  SetupPage,
  TasksPage,
  TeamPage,
  TodayPage,
} from "./pages/pages.js";
import { AccountSettingsPage } from "./settings/account-page.js";
import { SettingsIndex } from "./settings/settings-index.js";
import { SettingsLayout } from "./settings/settings-layout.js";
import { AppShell } from "./shell/app-shell.js";
import { LockScreen } from "./quick-access/lock-screen.js";
import { SharedDeviceGate } from "./quick-access/shared-device-gate.js";
import { PublicReportPage } from "./reports/public-report-page.js";

type LooseSearch = Record<string, boolean | number | string>;

// Deep links and redirects pass query strings between pages; each page reads
// the keys it knows and ignores the rest.
function looseSearch(search: Record<string, unknown>): LooseSearch {
  const kept: LooseSearch = {};
  for (const [key, value] of Object.entries(search)) {
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      kept[key] = value;
    }
  }
  return kept;
}

function RootComponent() {
  return <Outlet />;
}

function RootError({ error, reset }: ErrorComponentProps) {
  return (
    <main className="grid min-h-dvh place-items-center bg-canvas p-5">
      <div className="w-full max-w-2xl">
        <ErrorState message={displayError(error)} onAction={reset} />
      </div>
    </main>
  );
}

const rootRoute = createRootRoute({
  component: RootComponent,
  errorComponent: RootError,
  notFoundComponent: () => (
    <main className="grid min-h-dvh place-items-center bg-canvas p-5 text-center">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">
          That page isn’t here
        </h1>
        <Link className={cn(buttonVariants(), "mt-5")} to="/">
          Today
        </Link>
      </div>
    </main>
  ),
});

function AuthRoute({ mode }: { mode: "sign-in" | "sign-up" }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return (
    <AuthScreen
      mode={mode}
      onAuthenticated={async () => {
        await queryClient.invalidateQueries({ queryKey: ["me"] });
        await navigate({ to: "/" });
      }}
    />
  );
}

const signInRoute = createRoute({
  component: () => <AuthRoute mode="sign-in" />,
  getParentRoute: () => rootRoute,
  path: "/sign-in",
});

const signUpRoute = createRoute({
  component: () => <AuthRoute mode="sign-up" />,
  getParentRoute: () => rootRoute,
  path: "/sign-up",
});

const forgotPasswordRoute = createRoute({
  component: ForgotPasswordScreen,
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
});

function AuthCallbackRoute() {
  const invitationToken = pendingInvitationToken();
  return invitationToken ? (
    <Navigate
      replace
      search={{ token: invitationToken }}
      to="/invitations/accept"
    />
  ) : (
    <Navigate replace to="/" />
  );
}

const authCallbackRoute = createRoute({
  component: AuthCallbackRoute,
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
});

const invitationAcceptRoute = createRoute({
  component: function InvitationRoute() {
    const search = invitationAcceptRoute.useSearch();
    return (
      <InvitationAcceptScreen
        {...(search.token ? { token: search.token } : {})}
      />
    );
  },
  getParentRoute: () => rootRoute,
  path: "/invitations/accept",
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.token === "string" ? { token: search.token } : {}),
  }),
});

const lockRoute = createRoute({
  component: LockScreen,
  getParentRoute: () => rootRoute,
  path: "/lock",
});

const publicReportRoute = createRoute({
  component: () => {
    const { token, slug, period } = publicReportRoute.useParams();
    return <PublicReportPage token={token} slug={slug} period={period} />;
  },
  getParentRoute: () => rootRoute,
  path: "/share/$slug/report/$period/$token",
});
const publicReportSectionRoute = createRoute({
  component: () => {
    const { token, slug, period, section } =
      publicReportSectionRoute.useParams();
    return (
      <PublicReportPage
        token={token}
        slug={slug}
        period={period}
        section={section}
      />
    );
  },
  getParentRoute: () => rootRoute,
  path: "/share/$slug/report/$period/$token/$section",
});

function AuthenticatedArea() {
  const meQuery = useQuery<MeResponse, Error>({
    queryFn: getCurrentUser,
    queryKey: ["me"],
    retry: shouldRetryCurrentUser,
    retryDelay: currentUserRetryDelay,
    staleTime: 60_000,
  });

  if (meQuery.isPending) {
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        <div className="w-full max-w-xl">
          <LoadingState label="Opening SafePlate" waking />
        </div>
      </main>
    );
  }
  if (meQuery.error) {
    if (meQuery.error instanceof ApiError && meQuery.error.status === 401) {
      return <Navigate replace to="/lock" />;
    }
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas p-5">
        <div className="w-full max-w-2xl">
          <ErrorState
            message={displayError(meQuery.error)}
            onAction={() => void meQuery.refetch()}
            title="Couldn’t open SafePlate"
          />
        </div>
      </main>
    );
  }

  if (meQuery.data.memberships.length === 0) {
    return (
      <OrganisationOnboarding
        me={meQuery.data}
        onCreated={async () => {
          await meQuery.refetch();
        }}
      />
    );
  }

  if (meQuery.data.session.auth_method === "shared_pin") {
    return <AppShell me={meQuery.data} />;
  }

  return (
    <SharedDeviceGate me={meQuery.data}>
      {(scopedMe) => <AppShell me={scopedMe} />}
    </SharedDeviceGate>
  );
}

const authenticatedRoute = createRoute({
  component: AuthenticatedArea,
  getParentRoute: () => rootRoute,
  id: "_authenticated",
});

const todayRoute = createRoute({
  component: TodayPage,
  getParentRoute: () => authenticatedRoute,
  path: "/",
});
const recordsRoute = createRoute({
  component: RecordsPage,
  getParentRoute: () => authenticatedRoute,
  path: "/records",
  validateSearch: looseSearch,
});
const loggingRoute = createRoute({
  component: LoggingPage,
  getParentRoute: () => authenticatedRoute,
  path: "/log",
  validateSearch: looseSearch,
});
const attentionRoute = createRoute({
  component: AttentionPage,
  getParentRoute: () => authenticatedRoute,
  path: "/attention",
  validateSearch: looseSearch,
});
const tasksRoute = createRoute({
  component: TasksPage,
  getParentRoute: () => authenticatedRoute,
  path: "/tasks",
  validateSearch: looseSearch,
});
const teamRoute = createRoute({
  component: TeamPage,
  getParentRoute: () => authenticatedRoute,
  path: "/team",
  validateSearch: looseSearch,
});
const setupRoute = createRoute({
  component: SetupPage,
  getParentRoute: () => authenticatedRoute,
  path: "/setup",
  validateSearch: looseSearch,
});
const reportsRoute = createRoute({
  component: ReportsPage,
  getParentRoute: () => authenticatedRoute,
  path: "/reports",
  validateSearch: looseSearch,
});
// A printable QR label. The shell shows it without its navigation.
const assetLabelRoute = createRoute({
  component: function AssetLabelRoute() {
    const { assetId } = assetLabelRoute.useParams();
    const search = assetLabelRoute.useSearch();
    return (
      <AssetLabelPage
        assetId={assetId}
        {...(search.token ? { token: search.token } : {})}
      />
    );
  },
  getParentRoute: () => authenticatedRoute,
  path: "/labels/assets/$assetId",
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.token === "string" ? { token: search.token } : {}),
  }),
});
// Every fridge's label at a location, for printing in one go.
const assetLabelsRoute = createRoute({
  component: function AssetLabelsRoute() {
    const search = assetLabelsRoute.useSearch();
    return search.location_id ? (
      <AssetLabelsPage locationId={search.location_id} />
    ) : (
      <Navigate replace search={{ section: "equipment" }} to="/setup" />
    );
  },
  getParentRoute: () => authenticatedRoute,
  path: "/labels/assets",
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.location_id === "string"
      ? { location_id: search.location_id }
      : {}),
  }),
});
const inboxRoute = createRoute({
  component: InboxPage,
  getParentRoute: () => authenticatedRoute,
  path: "/inbox",
  validateSearch: looseSearch,
});

const settingsRoute = createRoute({
  component: SettingsLayout,
  getParentRoute: () => authenticatedRoute,
  path: "/settings",
});
const settingsIndexRoute = createRoute({
  component: SettingsIndex,
  getParentRoute: () => settingsRoute,
  path: "/",
});
const settingsNotificationsRoute = createRoute({
  component: NotificationsSettingsPage,
  getParentRoute: () => settingsRoute,
  path: "/notifications",
  validateSearch: looseSearch,
});
const settingsComputerRoute = createRoute({
  component: ComputerSettingsPage,
  getParentRoute: () => settingsRoute,
  path: "/computer",
  validateSearch: looseSearch,
});
const settingsBusinessRoute = createRoute({
  component: BusinessSettingsPage,
  getParentRoute: () => settingsRoute,
  path: "/business",
  validateSearch: looseSearch,
});
const settingsPlanRoute = createRoute({
  component: PlanSettingsPage,
  getParentRoute: () => settingsRoute,
  path: "/plan",
  validateSearch: looseSearch,
});
const settingsDataRoute = createRoute({
  component: DataSettingsPage,
  getParentRoute: () => settingsRoute,
  path: "/data",
  validateSearch: looseSearch,
});
const settingsAccountRoute = createRoute({
  component: AccountSettingsPage,
  getParentRoute: () => settingsRoute,
  path: "/account",
});
const settingsPrivacyRoute = createRoute({
  component: PrivacySettingsPage,
  getParentRoute: () => settingsRoute,
  path: "/privacy",
  validateSearch: looseSearch,
});

// Old addresses keep working; their query strings travel with them.
const billingRoute = createRoute({
  beforeLoad: ({ search }) => {
    throw redirect({ replace: true, search, to: "/settings/plan" });
  },
  getParentRoute: () => rootRoute,
  path: "/billing",
  validateSearch: looseSearch,
});
const billingSuccessRoute = createRoute({
  beforeLoad: ({ search }) => {
    throw redirect({
      replace: true,
      search: { ...search, checkout: "success" },
      to: "/settings/plan",
    });
  },
  getParentRoute: () => rootRoute,
  path: "/billing/success",
  validateSearch: looseSearch,
});
const privacyRoute = createRoute({
  beforeLoad: ({ search }) => {
    throw redirect({ replace: true, search, to: "/settings/privacy" });
  },
  getParentRoute: () => rootRoute,
  path: "/privacy",
  validateSearch: looseSearch,
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  signUpRoute,
  forgotPasswordRoute,
  authCallbackRoute,
  invitationAcceptRoute,
  lockRoute,
  publicReportRoute,
  publicReportSectionRoute,
  billingRoute,
  billingSuccessRoute,
  privacyRoute,
  authenticatedRoute.addChildren([
    todayRoute,
    recordsRoute,
    loggingRoute,
    attentionRoute,
    tasksRoute,
    teamRoute,
    setupRoute,
    reportsRoute,
    inboxRoute,
    assetLabelRoute,
    assetLabelsRoute,
    settingsRoute.addChildren([
      settingsIndexRoute,
      settingsNotificationsRoute,
      settingsComputerRoute,
      settingsBusinessRoute,
      settingsPlanRoute,
      settingsDataRoute,
      settingsAccountRoute,
      settingsPrivacyRoute,
    ]),
  ]),
]);

export const router = createRouter({
  defaultPreload: "intent",
  defaultPreloadStaleTime: 30_000,
  routeTree,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
